/**
 * /search Recipes panel — load-more + tag-fetches (Phase 5a)
 *
 * Direct mirror of aro-search-page-blogs.js with recipe-specific
 * constants (path prefix, tags blob ID). See that file for full
 * pipeline notes. Phase 5b will add recipe-scope filters
 * (Title / Description / Body / Products) with per-article
 * classification via metafields.custom.recipe_data.
 *
 * Endpoint reuse: same predictive-search-tagged-recipes section (this
 * is what it was originally built for — recipe_data / recipe_jsonld
 * are populated for these articles, unlike blog articles where they
 * come back null).
 */
(function () {
  'use strict';

  var LOADING_LABEL = 'Loading…';
  var RECIPE_PATH = '/blogs/make-your-own-skin-care-recipes';
  var TAG_SECTION = 'predictive-search-tagged-recipes';
  var SCOPE_STORAGE_KEY = 'aro-search-page-recipe-scopes';
  var ALL_SCOPES = ['title', 'description', 'body', 'products'];
  var terms;
  var state;
  var cachedRecipeTags = null;
  // Stale-request guard — see aro-search-page-products.js for rationale.
  var requestId = 0;

  // ── Phase 8: scope-filter persistence (sessionStorage) ──

  function readSavedScopes() {
    try {
      var v = sessionStorage.getItem(SCOPE_STORAGE_KEY);
      if (!v) return null;
      var arr = JSON.parse(v);
      return Array.isArray(arr) ? arr.filter(function (s) { return ALL_SCOPES.indexOf(s) !== -1; }) : null;
    } catch (e) { return null; }
  }

  function writeSavedScopes(arr) {
    try { sessionStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(arr)); }
    catch (e) { /* silent */ }
  }

  function restoreScopeCheckboxes() {
    if (!state) return;
    var saved = readSavedScopes();
    if (!saved) return; // First visit — leave all checked (Liquid defaults).
    var savedSet = {};
    saved.forEach(function (s) { savedSet[s] = true; });
    ALL_SCOPES.forEach(function (s) {
      var cb = state.panel.querySelector('[data-aro-recipe-scope="' + s + '"]');
      if (cb) cb.checked = !!savedSet[s];
    });
  }

  function currentActiveScopes() {
    if (!state) return ALL_SCOPES.slice();
    return ALL_SCOPES.filter(function (s) {
      var cb = state.panel.querySelector('[data-aro-recipe-scope="' + s + '"]');
      return cb && cb.checked;
    });
  }

  function getTerms() {
    return new URLSearchParams(location.search).get('q') || '';
  }

  function urlKey(a) {
    var href = a && a.getAttribute('href');
    return href ? href.split('?')[0] : null;
  }

  function collectUrls(root) {
    var set = new Set();
    if (!root) return set;
    root.querySelectorAll('a[href^="/blogs/"]').forEach(function (a) {
      var k = urlKey(a);
      if (k) set.add(k);
    });
    return set;
  }

  function fetchPage(page) {
    var url = '/search?q=' + encodeURIComponent(terms) + '&type=article&page=' + page;
    return fetch(url).then(function (r) { return r.text(); }).then(function (html) {
      return new DOMParser().parseFromString(html, 'text/html');
    });
  }

  // ── Tag-matched articles ──

  // pdp-load-audit 2026-09-22: #aro-recipe-tags no longer exists in the page —
  // the tag lists moved out of inline HTML (33.9 KB on EVERY page, read only on
  // demand) into sections/aro-search-tags.liquid. Fetched once per tab session
  // and cached in sessionStorage, shared with the header predictive search.
  // Returns a PROMISE of the recipe tag array; all failure paths resolve to []
  // exactly as the old `if (!el) return []` guard did.
  function getRecipeTagsData() {
    if (cachedRecipeTags) return cachedRecipeTags;
    var KEY = 'aro-search-tags';
    try {
      var cached = sessionStorage.getItem(KEY);
      if (cached) {
        cachedRecipeTags = Promise.resolve(JSON.parse(cached).recipes || []);
        return cachedRecipeTags;
      }
    } catch (e) { /* private mode / blocked storage */ }

    cachedRecipeTags = fetch('/?section_id=aro-search-tags')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sec = doc.getElementById('shopify-section-aro-search-tags');
        if (!sec) return [];
        var data;
        try { data = JSON.parse(sec.textContent.trim()); } catch (e) { return []; }
        if (!data || !data.recipes) return [];
        try { sessionStorage.setItem(KEY, JSON.stringify(data)); } catch (e) { /* quota */ }
        return data.recipes;
      })
      .catch(function () { return []; });
    return cachedRecipeTags;
  }

  // Now returns a Promise of handles.
  function getMatchingRecipeTagHandles(searchTerm) {
    var q = (searchTerm || '').toLowerCase().trim();
    if (!q) return Promise.resolve([]);
    return getRecipeTagsData().then(function (tags) {
      return tags
        .filter(function (t) { return (t.name || '').toLowerCase().indexOf(q) !== -1; })
        .map(function (t) { return t.handle; });
    });
  }

  function fetchTagMatchedRecipes(searchTerm) {
    return getMatchingRecipeTagHandles(searchTerm).then(function (handles) {
    if (handles.length === 0) return Promise.resolve([]);
    var fetches = handles.map(function (h) {
      var url = RECIPE_PATH + '/tagged/' + encodeURIComponent(h) + '?section_id=' + TAG_SECTION;
      return fetch(url).then(function (r) {
        return r.ok ? r.text() : '';
      }).then(function (html) {
        if (!html) return [];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var sec = doc.getElementById('shopify-section-' + TAG_SECTION);
        if (!sec) return [];
        try { return JSON.parse(sec.textContent.trim()) || []; }
        catch (e) { return []; }
      }).catch(function () { return []; });
    });
    return Promise.all(fetches).then(function (arrays) {
      return arrays.reduce(function (acc, arr) { return acc.concat(arr); }, []);
    });
    });
  }

  // Scope classification — union sources for each scope (matches Liquid
  // computation in main-search.liquid and predictive-search.js scopesFor).
  //   Title    → article.title + recipe_data.name
  //   Desc     → article.summary (+ summary_html if present)
  //   Body     → recipe_data.methodSteps + article.body (stripped)
  //   Products → recipe_data.phases[].ingredients[].{name,productHandle}
  //              + article.tags
  function stripHtml(s) {
    return (s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  }
  function scopesFor(a, qLower) {
    var rd = a.recipe_data || null;
    var titleText = ((a.title || '') + ' ' + (rd && rd.name || '')).toLowerCase();
    var title = titleText.indexOf(qLower) !== -1;
    var descText = (stripHtml(a.summary || '') + ' ' + stripHtml(a.summary_html || '')).toLowerCase();
    var description = descText.indexOf(qLower) !== -1;
    var bodyPieces = [];
    if (rd && rd.methodSteps && rd.methodSteps.length) bodyPieces.push(rd.methodSteps.join(' '));
    if (a.body) bodyPieces.push(stripHtml(a.body));
    var body = bodyPieces.join(' ').toLowerCase().indexOf(qLower) !== -1;
    var products = false;
    if (rd && rd.phases && rd.phases.length) {
      products = rd.phases.some(function (phase) {
        return (phase.ingredients || []).some(function (ing) {
          var n = (ing.name || '').toLowerCase();
          var h = (ing.productHandle || '').toLowerCase();
          return n.indexOf(qLower) !== -1 || (h && h.indexOf(qLower) !== -1);
        });
      });
    }
    if (!products) {
      products = (a.tags || []).some(function (t) { return (t || '').toLowerCase().indexOf(qLower) !== -1; });
    }
    return { title: title, description: description, body: body, products: products };
  }

  function articleJsonToHtml(article) {
    var url = article.url || '';
    var title = article.title || '';
    var image = article.image || '';
    var escapedTitle = title.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    var imgHtml = image
      ? '<img src="' + image + '" alt="' + escapedTitle + '" loading="lazy">'
      : '';
    var s = scopesFor(article, (terms || '').toLowerCase().trim());
    return (
      '<li class="columns"' +
        ' data-scope-title="' + (s.title ? 1 : 0) + '"' +
        ' data-scope-description="' + (s.description ? 1 : 0) + '"' +
        ' data-scope-body="' + (s.body ? 1 : 0) + '"' +
        ' data-scope-products="' + (s.products ? 1 : 0) + '">' +
        '<div class="blog-post aro-search-page__recipe-post--tag-matched">' +
          '<figure class="featured-image aspect-ratio" style="--ratio-percent: 100%;">' +
            '<a href="' + url + '" title="' + escapedTitle + '">' + imgHtml + '</a>' +
          '</figure>' +
          '<h5><a href="' + url + '" title="' + escapedTitle + '">' + escapedTitle + '</a></h5>' +
        '</div>' +
      '</li>'
    );
  }

  // Scope filter — recomputes state.filteredLis from state.allLis based
  // on active scope checkboxes, resets to page 1, and re-renders (which
  // in turn rebuilds pagination based on filtered count).
  function applyScopeFilter() {
    if (!state || !state.allLis) return;
    var activeScopes = ['title','description','body','products'].filter(function (s) {
      var cb = state.panel.querySelector('[data-aro-recipe-scope="' + s + '"]');
      return cb && cb.checked;
    });
    state.filteredLis = state.allLis.filter(function (li) {
      // Edge case: li has no scope-* attrs (server-side classification
      // missed it) — always include so real matches aren't hidden.
      var anyAttr = ['title','description','body','products'].some(function (s) {
        return li.hasAttribute('data-scope-' + s);
      });
      if (!anyAttr) return true;
      return activeScopes.some(function (s) {
        return li.getAttribute('data-scope-' + s) === '1';
      });
    });
    state.totalItems = state.filteredLis.length;
    if (state.countEl) {
      var suffix = '';
      if (state && state.__originalTerms) {
        suffix = ' (corrected from ‘' + state.__originalTerms + '’)';
      }
      state.countEl.textContent = state.totalItems + ' Recipe' +
        (state.totalItems === 1 ? '' : 's') + ' for ‘' + terms + '’' + suffix;
    }
    renderCurrentPage(1);
  }

  function bindScopeCheckboxes() {
    if (!state) return;
    state.panel.querySelectorAll('[data-aro-recipe-scope]').forEach(function (cb) {
      if (cb.__aroBound) return;
      cb.__aroBound = true;
      cb.addEventListener('change', function () {
        writeSavedScopes(currentActiveScopes());
        applyScopeFilter();
      });
    });
  }

  var PAGE_SIZE = 15;

  // Blind scan pages 1-MAX_PAGES — Shopify's /search?type=article
  // pagination links are unreliable (missing on some responses), so
  // maxPage detection under-fetches. See same fix in aro-search-page-
  // blogs.js. Dedupe happens later in eagerReplace's `seen` set.
  function fetchAllArticleLis() {
    var MAX_PAGES = 10;
    return fetchInBatches(1, MAX_PAGES, 3);
  }

  function fetchInBatches(startPage, endPage, batchSize) {
    var pages = [];
    for (var p = startPage; p <= endPage; p++) pages.push(p);
    var out = [];
    function runBatch(offset) {
      if (offset >= pages.length) return Promise.resolve();
      var batch = pages.slice(offset, offset + batchSize);
      return Promise.all(batch.map(function (page) {
        return fetchPage(page).then(extractLis).catch(function () { return []; });
      })).then(function (results) {
        results.forEach(function (lis) { out = out.concat(lis); });
        return runBatch(offset + batchSize);
      });
    }
    return runBatch(0).then(function () { return out; });
  }

  function extractLis(doc) {
    if (!doc) return [];
    var grid = doc.querySelector('.aro-search-page__recipes-grid');
    if (!grid) return [];
    return Array.prototype.slice.call(grid.children);
  }

  // Derive a corrected term from Shopify's fuzzy-matched suggest.json
  // results when queries[] is empty (Shopify doesn't always expose the
  // corrected query string, e.g. "vitamine a" returns 0 queries[] entries
  // even though products/articles are fuzzy-matched with titles starting
  // with "Vitamin A ..."). Take the first N words of the top-ranked
  // fuzzy-matched title where N = raw query word count.
  function deriveCorrectedFromTitles(query, titles) {
    if (!titles || titles.length === 0) return null;
    var queryWords = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (queryWords.length === 0) return null;
    var topWords = (titles[0] || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (topWords.length === 0) return null;
    var take = Math.min(queryWords.length, topWords.length);
    var candidate = topWords.slice(0, take).join(' ');
    // Reject if identical to original (case-insensitive) — no correction.
    if (candidate === queryWords.join(' ').toLowerCase()) return null;
    return candidate;
  }

  // Fuzzy-corrected term via /search/suggest.json when the initial fetch
  // yields no filtered recipes (or 0 results total). Guards against
  // infinite recursion via state.__fuzzyAttempted. Preserves scope-filter
  // correctness by re-fetching with the corrected term so the server
  // classifier re-runs on the actual matching text.
  function attemptFuzzyCorrection(myId) {
    if (!state || state.id !== myId) return;
    if (state.__fuzzyAttempted) return;
    state.__fuzzyAttempted = true;
    var originalTerms = terms;
    // Ask suggest.json for BOTH queries[] AND product/article fuzzy hits.
    // Shopify's queries[] is often empty for uncommon misspellings, so we
    // extract from the top fuzzy-matched titles as fallback.
    var url = '/search/suggest.json?q=' + encodeURIComponent(originalTerms) +
              '&resources[limit]=3';
    fetch(url, { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!state || state.id !== myId) return;
        var results = data && data.resources && data.resources.results;
        if (!results) return;
        var corrected = null;
        // Priority 1: use Shopify's own query correction if available.
        var queries = results.queries || [];
        if (queries.length > 0 && queries[0].text) {
          var q = queries[0].text.trim();
          if (q && q.toLowerCase() !== originalTerms.toLowerCase()) corrected = q;
        }
        // Priority 2: derive from top fuzzy-matched product/article/collection titles.
        if (!corrected) {
          var titles = [];
          (results.products || []).forEach(function (p) { if (p && p.title) titles.push(p.title); });
          (results.articles || []).forEach(function (a) { if (a && a.title) titles.push(a.title); });
          (results.collections || []).forEach(function (c) { if (c && c.title) titles.push(c.title); });
          corrected = deriveCorrectedFromTitles(originalTerms, titles);
        }
        if (!corrected) return;
        // Remember original for the "corrected from X" label.
        state.__originalTerms = originalTerms;
        // Update global `terms` (used throughout scope classifiers, fetch
        // URLs, count labels, empty-state message) and re-run the full
        // fetch pipeline. Bump requestId so any stale-in-flight callback
        // from the original request is ignored.
        terms = corrected;
        var newId = ++requestId;
        state.id = newId;
        state.allLis = null;
        state.filteredLis = null;
        // Clear any "No recipes found" empty-state message from the
        // previous attempt.
        var oldMsg = state.panel.querySelector('.aro-search-page__panel-empty');
        if (oldMsg) oldMsg.remove();
        state.panel.classList.add('aro-search-page__panel--loading');
        eagerReplace(newId);
      })
      .catch(function () { /* silent */ });
  }

  function eagerReplace(myId) {
    if (!state) return;
    Promise.all([
      fetchAllArticleLis().catch(function () { return []; }),
      fetchTagMatchedRecipes(terms),
    ]).then(function (results) {
      if (state && state.id !== myId) return; // superseded by newer init
      var shopifyLis = results[0] || [];
      var tagArticles = results[1] || [];

      if (!state.grid) {
        var empty = state.panel.querySelector('.aro-search-page__panel-empty');
        if (empty) empty.remove();
        var mainCol = state.panel.querySelector('.sidebar-container--products') || state.panel;
        var ul = document.createElement('ul');
        ul.className = 'aro-search-page__recipes-grid products collection row small-up-2 medium-up-2 large-up-3';
        // Insert BEFORE pagination-mount so grid renders above pagination.
        // Pagination-mount is server-rendered as last child; if we just
        // appendChild the grid ends up below it visually.
        var pagMount = mainCol.querySelector('.aro-search-page__pagination-mount');
        if (pagMount && pagMount.parentElement === mainCol) {
          mainCol.insertBefore(ul, pagMount);
        } else {
          mainCol.appendChild(ul);
        }
        state.grid = ul;
      }

      var seen = new Set();
      var mergedLis = [];
      tagArticles.forEach(function (a) {
        if (!a || !a.url) return;
        var key = a.url.split('?')[0];
        if (seen.has(key)) return;
        seen.add(key);
        var tmp = document.createElement('template');
        tmp.innerHTML = articleJsonToHtml(a);
        mergedLis.push(tmp.content.firstElementChild);
      });
      shopifyLis.forEach(function (li) {
        var link = li.querySelector('a[href^="/blogs/"]');
        var key = urlKey(link);
        if (!key || seen.has(key)) return;
        seen.add(key);
        mergedLis.push(li);
      });

      state.allLis = mergedLis;

      if (mergedLis.length === 0) {
        state.totalItems = 0;
        state.grid.remove();
        state.grid = null;
        var msg = document.createElement('div');
        msg.className = 'aro-search-page__panel-empty';
        var emptySuffix = state.__originalTerms ? ' (corrected from ‘' + state.__originalTerms + '’)' : '';
        msg.textContent = 'No recipes found for ‘' + terms + '’' + emptySuffix;
        var mainColMsg = state.panel.querySelector('.sidebar-container--products') || state.panel;
        mainColMsg.appendChild(msg);
        if (state.countEl) {
          var suffix0 = state.__originalTerms ? ' (corrected from ‘' + state.__originalTerms + '’)' : '';
          state.countEl.textContent = '0 Recipes for ‘' + terms + '’' + suffix0;
        }
        // Zero results at all — try a fuzzy-corrected term via
        // /search/suggest.json (see attemptFuzzyCorrection for details).
        attemptFuzzyCorrection(myId);
      } else {
        // applyScopeFilter computes filteredLis + totalItems from allLis
        // based on current scope checkbox state, updates count, and renders.
        applyScopeFilter();
        // Server-side scope classification uses exact-substring against
        // the raw user query. When Shopify fuzzy-matched articles (e.g.
        // "vitamine a" → "vitamin a"), all data-scope-* attrs come back
        // "0" so the scope filter hides everything even though allLis has
        // fetched hits. Try a fuzzy-corrected term and re-fetch, which
        // makes the server re-classify against the corrected term.
        if (state.filteredLis && state.filteredLis.length === 0) {
          attemptFuzzyCorrection(myId);
        }
      }

      state.panel.classList.remove('aro-search-page__panel--loading');
      // Notify sibling panels — see priority tab-load logic below.
      _dispatchActiveTabDoneOnce();
    }).catch(function () {
      if (state && state.id !== myId) return;
      if (state && state.panel) state.panel.classList.remove('aro-search-page__panel--loading');
      // Dispatch even on failure so siblings don't wait forever.
      _dispatchActiveTabDoneOnce();
    });
  }

  function renderCurrentPage(page) {
    if (!state || !state.grid) return;
    // Slice from filteredLis (scope-filtered) rather than allLis so scope
    // toggles change pagination + rendered slice, not just DOM visibility.
    var source = state.filteredLis || state.allLis;
    if (!source) return;
    state.currentPage = page;
    var start = (page - 1) * PAGE_SIZE;
    var slice = source.slice(start, start + PAGE_SIZE);
    state.grid.innerHTML = '';
    slice.forEach(function (li) { state.grid.appendChild(li.cloneNode(true)); });
    buildPagination();
  }

  function buildPagination() {
    if (!state) return;
    var mount = state.panel.querySelector('.aro-search-page__pagination-mount');
    if (!mount) return;
    var totalPages = Math.ceil(state.totalItems / PAGE_SIZE);
    if (totalPages <= 1) { mount.innerHTML = ''; return; }
    mount.innerHTML = renderThemePagination(state.currentPage, totalPages, state.totalItems, 'recipe');
    bindPaginationClicks();
  }

  function bindPaginationClicks() {
    if (!state) return;
    var mount = state.panel.querySelector('.aro-search-page__pagination-mount');
    if (!mount) return;
    mount.querySelectorAll('a[data-page]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        if (a.parentElement.classList.contains('disabled')) return;
        var page = parseInt(a.getAttribute('data-page'), 10);
        if (isNaN(page) || page < 1 || page > Math.ceil(state.totalItems / PAGE_SIZE)) return;
        renderCurrentPage(page);
        state.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function renderThemePagination(current, totalPages, totalItems, singular) {
    var start = (current - 1) * PAGE_SIZE + 1;
    var end = Math.min(current * PAGE_SIZE, totalItems);
    var progress = totalItems > 0 ? end / totalItems : 0;
    var plural = totalItems === 1 ? singular : singular + 's';
    var prevArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
    var nextArrow = '<svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
    var pageItems = '';
    for (var p = 1; p <= totalPages; p++) {
      if (p === current) {
        pageItems += '<li class="current"><span class="page">' + p + '</span></li>';
      } else {
        pageItems += '<li class="page"><a href="#" data-page="' + p + '">' + p + '</a></li>';
      }
    }
    return '<div class="pagination--wrapper">' +
      '<div class="thb-filter-count">' +
        '<span class="facets__label">' + start + '-' + end + ' of ' + totalItems + ' ' + plural + '</span>' +
        '<div class="pagination--progress" style="--progress-count: ' + progress + ';"><div class="pagination--progress-bar"></div></div>' +
      '</div>' +
      '<nav class="pagination pagination-type--paginated">' +
        '<div class="row"><div class="small-12 columns">' +
          '<ul class="page-numbers nav-links">' +
            '<li class="prev' + (current === 1 ? ' disabled' : '') + '"><a href="#" data-page="' + (current - 1) + '" aria-label="Previous">' + prevArrow + '</a></li>' +
            pageItems +
            '<li class="next' + (current === totalPages ? ' disabled' : '') + '"><a href="#" data-page="' + (current + 1) + '" aria-label="Next">' + nextArrow + '</a></li>' +
          '</ul>' +
        '</div></div>' +
      '</nav>' +
    '</div>';
  }

  // Removed: loadMore (superseded by client-side pagination).

  function init() {
    var panel = document.querySelector('.aro-search-page__panel--recipes');
    if (!panel) { state = null; return; }
    terms = getTerms();
    if (!terms) { state = null; return; }

    var grid = panel.querySelector('.aro-search-page__recipes-grid');
    var countEl = panel.querySelector('.aro-search-page__recipes-count');

    var myId = ++requestId;
    state = {
      panel: panel,
      grid: grid,
      countEl: countEl,
      button: null,
      currentPage: 1,
      seenUrls: collectUrls(grid),
      loading: false,
      id: myId,
    };

    bindScopeCheckboxes();
    restoreScopeCheckboxes();
    applyScopeFilter();
    eagerReplace(myId);
  }

  function hookRerender() {
    if (!window.FacetFiltersForm) return false;
    if (FacetFiltersForm.__aroRecipesHooked) return true;
    FacetFiltersForm.__aroRecipesHooked = true;
    var orig = FacetFiltersForm.renderProductGridContainer;
    if (typeof orig !== 'function') return true;
    FacetFiltersForm.renderProductGridContainer = function (html) {
      orig.call(this, html);
      // See aro-search-page-products.js for rationale: defer init so
      // updateURLHash (sync-after-forEach in renderPage) runs first —
      // otherwise cached-response paths make us read stale location.search.
      setTimeout(function () { try { init(); } catch (e) { /* silent */ } }, 0);
    };
    return true;
  }

  // Priority tab-load — see aro-search-page-products.js for rationale.
  var _MY_TAB_NAME = 'recipes';
  var _activeTabDispatchedOnce = false;
  function _isActiveTab() {
    var stored = null;
    try { stored = sessionStorage.getItem('aro-search-page-tab'); } catch (e) {}
    return (stored || 'products') === _MY_TAB_NAME;
  }
  function _dispatchActiveTabDoneOnce() {
    if (_activeTabDispatchedOnce) return;
    _activeTabDispatchedOnce = true;
    try { window.dispatchEvent(new Event('aro:search-active-tab-done')); } catch (e) {}
  }

  function bootstrap() {
    if (!hookRerender()) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (hookRerender() || tries > 40) clearInterval(iv);
      }, 100);
    }
    if (_isActiveTab()) {
      init();
    } else {
      window.addEventListener('aro:search-active-tab-done', function () {
        init();
      }, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
